import React, { useState, useRef, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { compressVideo } from './lib/compressVideo';
import BodyPatternTicks from './components/BodyPatternTicks';
import './VideoUpload.css';

const ANALYZE_API = 'https://horse-biomechanics.vercel.app/api/analyze';
const DLC_API = 'https://epona001-tru-stride-analyzer.hf.space/api/analyze';
const SUPABASE_URL = 'https://fyyaxkmtvnvpahmqwiyu.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ5eWF4a210dm52cGFobXF3aXl1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwMDMyMTUsImV4cCI6MjA4OTU3OTIxNX0.Ki9BDBBLkYf1UccIGXK578wI0X3D4f5aj9B2RJKzgxA';

const VideoUpload = ({ user }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState(''); // 'uploading' | 'analyzing' | 'complete'
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [resultId] = useState(() => `TS-${Date.now()}`);
  const xhrRef = useRef(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [horses, setHorses] = useState([]);
  const [selectedHorseId, setSelectedHorseId] = useState('');
  const [selectedGaitType, setSelectedGaitType] = useState('trot');
  const [newHorseName, setNewHorseName] = useState('');
  const [savingHorse, setSavingHorse] = useState(false);
  const [videoNotes] = useState('');
  const [uploadedVideoId, setUploadedVideoId] = useState(null);

  // Load user's horses when logged in
  useEffect(() => {
    if (user) {
      supabase
        .from('horses')
        .select('id, name')
        .eq('owner_id', user.id)
        .order('name')
        .then(({ data }) => {
          setHorses(data || []);
        });
    }
  }, [user]);

  const showToast = (msg, type = 'info') => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const validateFile = (file) => {
    if (!file) return 'No file selected.';
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/avi', 'video/x-msvideo', 'video/mov'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a video file (MP4, MOV, or AVI).';
    }
    if (file.size > 500 * 1024 * 1024) {
      return 'File is too large. Maximum size is 500MB. Try trimming the video to under 2 minutes.';
    }
    return null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const validationError = validateFile(e.target.files[0]);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(e.target.files[0]);
    }
  };

  const cancelUpload = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setAnalysisStatus('');
  };

  const runAnalysisAndSave = async (fileUrl, filePath, horseName) => {
    try {
      // ── Try real DLC API first (HuggingFace Space with GPU) ──
      let analysis = null;
      let isAbnormal = false;
      let usedDlc = false;

      try {
        // Download the video and send to DLC API as multipart upload
        const videoBlob = await fetch(fileUrl).then(r => r.blob());
        const formData = new FormData();
        formData.append('video', videoBlob, selectedFile.name);

        const dlcResponse = await Promise.race([
          fetch(DLC_API, { method: 'POST', body: formData }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('DLC timeout')), 120000)),
        ]);

        if (dlcResponse.ok) {
          const dlcResult = await dlcResponse.json();
          // DLC API returns the Supabase-compatible schema directly
          analysis = {
            analysis_id: `DLC-${Date.now()}`,
            timestamp: dlcResult.metadata?.analyzed_at || new Date().toISOString(),
            overall_score: dlcResult.soundness_score,
            overall_assessment: dlcResult.is_abnormal
              ? `${dlcResult.findings.length} abnormality(ies) detected with ${dlcResult.overall_severity} severity. ${dlcResult.recommendations[0] || ''}`
              : 'Analysis indicates normal gait pattern with no significant abnormalities.',
            gait_metrics: dlcResult.biomechanics_data,
            abnormalities: dlcResult.findings,
            keypoint_data: { total_frames: dlcResult.metadata?.num_frames },
            // Pass through raw DLC data for DB save
            _dlc_raw: dlcResult,
          };
          isAbnormal = dlcResult.is_abnormal;
          usedDlc = true;
          console.log('✅ Real DLC analysis completed — soundness:', dlcResult.soundness_score);
        } else {
          console.warn('DLC API returned error, falling back to Vercel endpoint');
        }
      } catch (dlcErr) {
        console.warn('DLC API unavailable, falling back to Vercel endpoint:', dlcErr.message);
      }

      // ── Fallback to existing Vercel analyze endpoint ──
      if (!analysis) {
        const response = await fetch(ANALYZE_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_url: fileUrl,
            file_path: filePath,
            file_name: selectedFile.name,
            horse_name: horseName,
            gait_type: selectedGaitType,
            notes: videoNotes,
          }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({ error: 'Analysis failed' }));
          throw new Error(errData.error || 'Analysis failed on server.');
        }

        const result = await response.json();
        analysis = result.analysis;
        isAbnormal = analysis.overall_score < 96;
      }

      // Save to database if user is logged in
      if (user) {
        try {
          // Verify we have an active session
          const { data: sessionData } = await supabase.auth.getSession();
          const activeSession = sessionData?.session;

          if (activeSession) {
            const videoInsert = {
              owner_id: user.id,
              filename: selectedFile.name,
              file_url: fileUrl,
              file_size_bytes: selectedFile.size,
              status: 'analyzed',
              gait_type: selectedGaitType,
              notes: videoNotes,
            };
            if (selectedHorseId) {
              videoInsert.horse_id = selectedHorseId;
            }

            const { data: videoRecord, error: videoErr } = await supabase
              .from('videos')
              .insert(videoInsert)
              .select()
              .single();

            if (videoErr) {
              console.error('Error saving video:', videoErr.message, videoErr);
            }

            if (videoRecord) {
              setUploadedVideoId(videoRecord.id);
              // Use DLC raw data if available for richer reports
              const dlcRaw = analysis._dlc_raw;
              const reportInsert = {
                video_id: videoRecord.id,
                owner_id: user.id,
                soundness_score: dlcRaw ? dlcRaw.soundness_score : analysis.overall_score,
                overall_severity: dlcRaw ? dlcRaw.overall_severity : (isAbnormal ? 'mild' : 'normal'),
                is_abnormal: isAbnormal,
                findings: dlcRaw ? dlcRaw.findings : (analysis.abnormalities || []),
                biomechanics_data: dlcRaw ? dlcRaw.biomechanics_data : (analysis.gait_metrics || {}),
                recommendations: dlcRaw
                  ? dlcRaw.recommendations
                  : (analysis.abnormalities || []).filter(a => a.recommendation).map(a => a.recommendation),
                analyzed_at: new Date().toISOString(),
              };
              if (selectedHorseId) {
                reportInsert.horse_id = selectedHorseId;
              }

              const { error: reportErr } = await supabase
                .from('analysis_reports')
                .insert(reportInsert);

              if (reportErr) {
                console.error('Error saving report:', reportErr.message, reportErr);
              }
            }
          } else {
            console.warn('No active auth session — skipping DB save');
          }
        } catch (dbErr) {
          console.error('DB save error (non-blocking):', dbErr);
          // Don't block results display
        }
      }

      setUploading(false);
      setAnalysisStatus('complete');
      setAnalysisResult({
        id: analysis.analysis_id,
        result: isAbnormal ? 'abnormal' : 'normal',
        confidence: analysis.gait_metrics?.symmetry?.overall || 0.85,
        analysisTime: analysis.timestamp,
        details: analysis.overall_assessment,
        source: usedDlc ? 'deeplabcut' : 'huggingface',
        videoInfo: {
          frames: analysis.keypoint_data?.total_frames,
          score: analysis.overall_score,
        },
      });
      const dlcLabel = usedDlc ? '🧬 Real AI biomechanics analysis complete!' : 'Analysis complete!';
      showToast(user ? `${dlcLabel} Saved to your dashboard.` : dlcLabel, 'success');
    } catch (err) {
      setUploading(false);
      setAnalysisStatus('');
      setError(err.message || 'Analysis failed.');
    }
  };

  const addHorse = async (e) => {
    e.preventDefault();
    if (!user || !newHorseName.trim()) return;
    setSavingHorse(true);
    setError(null);
    const { data, error: horseError } = await supabase
      .from('horses')
      .insert({ owner_id: user.id, name: newHorseName.trim() })
      .select('id, name')
      .single();
    if (horseError) {
      setError(horseError.message);
    } else if (data) {
      setHorses((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedHorseId(data.id);
      setNewHorseName('');
    }
    setSavingHorse(false);
  };

  const analyzeStride = async () => {
    if (!selectedFile) return;
    if (user && !selectedHorseId) {
      setError('Add a horse profile and select it before uploading. That keeps videos organized when you have more than one horse.');
      return;
    }

    setAnalysisResult(null);
    setError(null);

    // Step 0: Compress video if over 50MB
    let fileToUpload = selectedFile;
    if (selectedFile.size > 50 * 1024 * 1024) {
      try {
        setCompressing(true);
        setCompressionProgress(0);
        showToast(`Compressing video (${formatFileSize(selectedFile.size)})...`, 'info');
        fileToUpload = await compressVideo(selectedFile, (pct) => setCompressionProgress(pct));
        setCompressing(false);

        if (fileToUpload.size > 50 * 1024 * 1024) {
          setError(`Video is still too large after compression (${formatFileSize(fileToUpload.size)}). Please trim to under 2 minutes and try again.`);
          return;
        }
        showToast(`Compressed: ${formatFileSize(selectedFile.size)} → ${formatFileSize(fileToUpload.size)}`, 'success');
      } catch (compErr) {
        setCompressing(false);
        setError(`Compression failed: ${compErr.message}. Try trimming the video manually.`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setAnalysisStatus('uploading');
    setUploadedVideoId(null);

    // Step 1: Upload video directly to Supabase Storage (bypasses Vercel 4.5MB limit)
    const timestamp = Date.now();
    const ext = fileToUpload.name.split('.').pop().toLowerCase();
    const filePath = `uploads/${timestamp}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(pct);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Upload successful — now call the analyze API and save to DB
        setUploadProgress(100);
        setAnalysisStatus('analyzing');

        const fileUrl = `${SUPABASE_URL}/storage/v1/object/public/videos/${filePath}`;
        const horseName = horses.find(h => h.id === selectedHorseId)?.name || 'Unknown Horse';

        runAnalysisAndSave(fileUrl, filePath, horseName);
      } else {
        setUploading(false);
        setAnalysisStatus('');
        let msg = `Upload failed (HTTP ${xhr.status})`;
        try {
          const errData = JSON.parse(xhr.responseText);
          msg = errData.message || errData.error || msg;
        } catch (_) {}
        setError(msg);
      }
    });

    xhr.addEventListener('error', () => {
      setUploading(false);
      setAnalysisStatus('');
      setError('Network error during upload. Please check your connection and try again.');
    });

    xhr.addEventListener('abort', () => {
      setUploading(false);
      setAnalysisStatus('');
      setError('Upload cancelled.');
    });

    // Upload directly to Supabase Storage
    // Use authenticated session token if logged in, anon key if not
    const getAuthToken = async () => {
      if (user) {
        const { data } = await supabase.auth.getSession();
        return data?.session?.access_token || SUPABASE_ANON_KEY;
      }
      return SUPABASE_ANON_KEY;
    };

    getAuthToken().then(token => {
      xhr.open('POST', `${SUPABASE_URL}/storage/v1/object/videos/${filePath}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
      xhr.setRequestHeader('Content-Type', fileToUpload.type || 'video/mp4');
      xhr.setRequestHeader('x-upsert', 'true');
      xhr.send(fileToUpload);
    });
  };

  // --- Result action handlers ---

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}?result=${resultId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('Link copied to clipboard!', 'success');
    } catch (_) {
      showToast(`Share this link: ${shareUrl}`, 'info');
    }
  };

  const handleSave = () => {
    if (!analysisResult) return;
    const report = formatReport(analysisResult, selectedFile.name);
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tru-stride-report-${resultId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Report saved!', 'success');
  };

  const handleExportVet = () => {
    if (!analysisResult) return;
    const report = formatVetReport(analysisResult, selectedFile.name);
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tru-stride-vet-export-${resultId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Vet export downloaded!', 'success');
  };

  const formatReport = (result, videoName) => {
    const verdict = result.result === 'normal' ? 'NORMAL STRIDE' : 'ABNORMAL STRIDE DETECTED';
    return [
      '══════════════════════════════════════════════',
      '           TRU-STRIDE ANALYSIS REPORT          ',
      '══════════════════════════════════════════════',
      '',
      `Report ID:    ${result.id}`,
      `Video:       ${videoName}`,
      `Date:        ${new Date(result.analysisTime).toLocaleString()}`,
      '',
      '──────────────────────────────────────────────',
      '                 RESULTS                       ',
      '──────────────────────────────────────────────',
      `Verdict:     ${verdict}`,
      `Confidence:  ${(result.confidence * 100).toFixed(0)}%`,
      `Source:      ${result.source === 'huggingface' ? 'AI Pose Estimation' : 'Demo Mode'}`,
      '',
      `Analysis:    ${result.details}`,
      '',
      '──────────────────────────────────────────────',
      '              IMPORTANT NOTICE                 ',
      '──────────────────────────────────────────────',
      'This analysis is for informational purposes only.',
      'It does NOT replace professional veterinary diagnosis.',
      'Please consult your veterinarian for any health concerns.',
      '',
      `Generated by Tru-Stride — ${new Date().toLocaleString()}`,
      '              tru-stride.net                   ',
    ].join('\n');
  };

  const formatVetReport = (result, videoName) => {
    return [
      '══════════════════════════════════════════════════════════',
      '          TRU-STRIDE — VETERINARY REFERRAL REPORT          ',
      '══════════════════════════════════════════════════════════',
      '',
      'PATIENT INFORMATION',
      '───────────────────────────────────────────────────────────',
      `Report ID:       ${result.id}`,
      `Video submitted: ${videoName}`,
      `Analysis date:   ${new Date(result.analysisTime).toLocaleString()}`,
      '',
      'AI STRIDE ANALYSIS FINDINGS',
      '───────────────────────────────────────────────────────────',
      `Classification:  ${result.result === 'normal' ? 'NORMAL STRIDE PATTERN' : 'ABNORMAL STRIDE PATTERN'}`,
      `AI confidence:   ${(result.confidence * 100).toFixed(0)}%`,
      `Analysis method: AI pose estimation via Tru-Stride ML`,
      '',
      `AI description: ${result.details}`,
      '',
      'CLINICAL NOTES',
      '───────────────────────────────────────────────────────────',
      'This report was generated by an AI-powered stride analysis tool.',
      'AI-generated classifications should be used as one input alongside',
      'physical examination, trot-up assessment, and diagnostic imaging',
      'before forming a clinical opinion.',
      '',
      'The treating veterinarian should form their own opinion based on',
      'direct clinical findings. Tru-Stride results should not be used',
      'as the sole basis for diagnosis or treatment decisions.',
      '',
      '───────────────────────────────────────────────────────────',
      'For more information: hello@tru-stride.net | tru-stride.net',
      `Report generated: ${new Date().toLocaleString()}`,
    ].join('\n');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const progressLabel = () => {
    if (analysisStatus === 'uploading') return `Uploading... ${uploadProgress}%`;
    if (analysisStatus === 'analyzing') return 'AI is analyzing stride pattern...';
    return '';
  };

  return (
    <div className="video-upload-container">
      {/* Toast notification */}
      {toastMsg && (
        <div className={`toast toast-${toastMsg.type}`}>
          {toastMsg.msg}
        </div>
      )}

      <div className="header">
        <div className="logo-container">
          <img src="/tru_stride_logo.svg" alt="Tru-Stride Logo" className="company-logo" />
        </div>
        <h1>Tru-Stride</h1>
        <p>AI-Powered Horse Stride Analysis</p>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="error-dismiss">✕</button>
        </div>
      )}

      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {!selectedFile ? (
          <>
            <div className="upload-icon">📹</div>
            <h3>Upload Horse Video</h3>
            <p>Drag and drop your video here, or click to browse</p>
            <p className="file-info">Supports MP4, MOV, AVI • Auto-compresses large files • 30–60 seconds recommended</p>
            <input type="file" accept="video/*" onChange={handleFileSelect} className="file-input" id="video-upload" />
            <label htmlFor="video-upload" className="upload-button">Choose Video File</label>
          </>
        ) : (
          <div className="file-preview">
            <div className="file-info-card">
              <div className="file-icon">🎬</div>
              <div className="file-details">
                <h4>{selectedFile.name}</h4>
                <p>{formatFileSize(selectedFile.size)}</p>
              </div>
              <button onClick={() => { setSelectedFile(null); setError(null); }} className="remove-button">✕</button>
            </div>

            {user && (
              <div className="video-options">
                <div className="option-group">
                  <label htmlFor="horse-select">Horse</label>
                  <select
                    id="horse-select"
                    value={selectedHorseId}
                    onChange={(e) => setSelectedHorseId(e.target.value)}
                    required
                  >
                    <option value="">Select a horse…</option>
                    {horses.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
                <div className="option-group">
                  <label htmlFor="gait-select">Gait</label>
                  <select id="gait-select" value={selectedGaitType} onChange={(e) => setSelectedGaitType(e.target.value)}>
                    <option value="walk">Walk</option>
                    <option value="trot">Trot</option>
                    <option value="canter">Canter</option>
                    <option value="gallop">Gallop</option>
                  </select>
                </div>
              </div>
            )}

            {user && (
              <form onSubmit={addHorse} className="add-horse-inline">
                <label htmlFor="new-horse-name">Or add a horse now</label>
                <div className="add-horse-row">
                  <input
                    id="new-horse-name"
                    type="text"
                    value={newHorseName}
                    onChange={(e) => setNewHorseName(e.target.value)}
                    placeholder="Horse name"
                    required
                  />
                  <button type="submit" className="auth-button" disabled={savingHorse || !newHorseName.trim()}>
                    {savingHorse ? 'Saving…' : 'Add horse'}
                  </button>
                </div>
                <p className="add-horse-hint">
                  Every video must belong to a horse so the dashboard stays organized.
                </p>
              </form>
            )}

            <button
              onClick={analyzeStride}
              disabled={uploading || compressing || (user && !selectedHorseId)}
              className={`analyze-button ${uploading || compressing ? 'analyzing' : ''}`}
            >
              {compressing ? '🗜️ Compressing...' : uploading ? '🔄 Analyzing...' : '🔍 Analyze Stride'}
            </button>
          </div>
        )}
      </div>

      {/* Compression progress */}
      {compressing && (
        <div className="analysis-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${compressionProgress}%`, transition: 'width 0.3s ease', background: '#FF9800' }}
            />
          </div>
          <p className="progress-label">🗜️ Compressing video... {compressionProgress}%</p>
        </div>
      )}

      {/* Real progress bar */}
      {uploading && (
        <div className="analysis-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease' }}
            />
          </div>
          <p className="progress-label">{progressLabel()}</p>
          <button onClick={cancelUpload} className="cancel-button">Cancel</button>
        </div>
      )}

      {/* Results */}
      {analysisResult && (
        <div className="results-container">
          <h3>📊 Stride Analysis Results</h3>
          <div className={`result-card ${analysisResult.result === 'normal' ? 'normal' : 'abnormal'}`}>
            <div className="result-header">
              <span className="result-icon">
                {analysisResult.result === 'normal' ? '✅' : '⚠️'}
              </span>
              <div>
                <h4>
                  Stride Pattern: {analysisResult.result === 'normal' ? 'Normal' : 'Abnormal'}
                </h4>
                <p>Confidence: {(analysisResult.confidence * 100).toFixed(0)}%</p>
              </div>
            </div>

            <div className="result-details">
              <p><strong>Analysis Time:</strong> {new Date(analysisResult.analysisTime).toLocaleString()}</p>
              <p><strong>Details:</strong> {analysisResult.details}</p>
              {analysisResult.source === 'deeplabcut' && (
                <p className="dlc-notice" style={{color: '#4CAF50', fontWeight: 'bold'}}>
                  🧬 Powered by DeepLabCut SuperAnimal — real biomechanics AI analysis
                </p>
              )}
              {analysisResult.source === 'fallback' && (
                <p className="demo-notice">
                  ⚠️ Demo mode — AI service was unavailable. Results may not reflect actual analysis.
                </p>
              )}
            </div>

            <div className="result-actions">
              <button className="secondary-button" onClick={handleShare} title="Copy shareable link">
                📤 Share Result
              </button>
              <button className="secondary-button" onClick={handleSave} title="Download analysis report">
                📋 Save to History
              </button>
              <button className="secondary-button" onClick={handleExportVet} title="Download vet summary">
                🏥 Export for Vet
              </button>
            </div>
          </div>
          {uploadedVideoId && user && (
            <BodyPatternTicks videoId={uploadedVideoId} user={user} />
          )}
        </div>
      )}

      <div className="disclaimer">
        <p><strong>Disclaimer:</strong> This tool is for informational purposes only and does not replace professional veterinary diagnosis. Always consult your veterinarian for health concerns.</p>
      </div>
    </div>
  );
};

export default VideoUpload;
